import React, { useEffect, useMemo, useRef } from "react";
import * as echarts from "echarts";
import { useTheme } from "@mui/material/styles";

const formatRangeLabel = (label) => {
  if (!label) return "";

  if (label.startsWith("--")) return `< ${label.replace("--", "-")} dBm`;
  if (label.endsWith("-")) return `> ${label.slice(0, -1)} dBm`;

  const match = label.match(/-?\d+/g);

  if (match?.length === 2) {
    const [a, b] = match.map(Number);
    return `${Math.min(a, b)} ~ ${Math.max(a, b)} dBm`;
  }

  return label;
};

const getThresholdValues = (data) => {
  const values = data
    .flatMap(([label]) => label.match(/-?\d+/g) || [])
    .map(Number);

  return [...new Set(values)].sort((a, b) => a - b);
};

const WirelessChannelInterfaceSliderChart = ({
  data = [],
  defaultThreshold = -60,
}) => {
  const theme = useTheme();
  const chartRef = useRef(null);
  const chartInstanceRef = useRef(null);

  const chartData = useMemo(() => {
    if (!Array.isArray(data)) return [];

    return data.map(([label, value]) => ({
      rawLabel: label,
      label: formatRangeLabel(label),
      value: Number(value) || 0,
    }));
  }, [data]);

  const maxValue = useMemo(() => {
    return Math.max(...chartData.map((item) => item.value), 0);
  }, [chartData]);

  const yAxisMax = useMemo(() => {
    return Math.ceil(maxValue / 10) * 10 || 10;
  }, [maxValue]);

  const thresholdValues = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return getThresholdValues(data);
  }, [data]);

  const thresholdIndex = useMemo(() => {
    const thresholdValue = Number(defaultThreshold);

    const index = thresholdValues.findIndex(
      (value) => value === thresholdValue,
    );

    return index >= 0 ? index : 0;
  }, [thresholdValues, defaultThreshold]);

  const safeThresholdIndex = Math.min(
    Math.max(thresholdIndex, 0),
    Math.max(thresholdValues.length - 1, 0),
  );

  const drawThresholdLine = (chart) => {
    if (!chart || chartData.length < 2 || thresholdValues.length === 0) return;

    const leftBarX = chart.convertToPixel(
      { xAxisIndex: 0 },
      safeThresholdIndex,
    );

    const rightBarX = chart.convertToPixel(
      { xAxisIndex: 0 },
      safeThresholdIndex + 1,
    );

    const x = (leftBarX + rightBarX) / 2;
    const yTop = chart.convertToPixel({ yAxisIndex: 0 }, yAxisMax);
    const yBottom = chart.convertToPixel({ yAxisIndex: 0 }, 0);

    chart.setOption({
      graphic: [
        {
          id: "thresholdLine",
          type: "line",
          shape: {
            x1: x,
            y1: yTop,
            x2: x,
            y2: yBottom,
          },
          style: {
            stroke: theme.palette.primary.main,
            lineWidth: 2,
          },
          silent: true,
        },
        {
          id: "thresholdTriangle",
          type: "polygon",
          shape: {
            points: [
              [x - 5, yTop - 8],
              [x + 5, yTop - 8],
              [x, yTop],
            ],
          },
          style: {
            fill: theme.palette.primary.redPrimary,
          },
          silent: true,
        },
      ],
    });
  };

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartInstanceRef.current || echarts.init(chartRef.current);
    chartInstanceRef.current = chart;

    const option = {
      animation: false,
      backgroundColor: theme.palette.primary.white,
      grid: {
        top: 20,
        left: 34,
        right: 20,
        bottom: 34,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        boundaryGap: true,
        data: chartData.map((item) => item.label),
        axisLine: {
          lineStyle: { color: theme.palette.primary.greyThread },
        },
        axisTick: { show: false },
        axisLabel: {
          interval: 0,
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
          margin: 12,
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "solid",
          },
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        interval: 10,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: theme.palette.primary.greyFourth,
          fontSize: theme.typography.customFontSize[0],
        },
        splitLine: {
          show: true,
          lineStyle: {
            color: theme.palette.primary.greyThread,
            type: "dotted",
          },
        },
      },
      tooltip: {
        trigger: "axis",
        backgroundColor: theme.palette.primary.blackTransparency,
        borderWidth: 0,
        textStyle: {
          color: theme.palette.primary.white,
          fontSize: theme.typography.customFontSize[0],
        },
        axisPointer: { type: "shadow" },
        formatter: (params) => {
          const item = params?.[0];
          if (!item) return "";
          return `${item.name}<br />Neighboring SSIDs: ${item.value}`;
        },
      },
      series: [
        {
          type: "bar",
          data: chartData.map((item) => item.value),
          barWidth: 36,
          itemStyle: {
            color: theme.palette.primary.mainHover,
          },
        },
      ],
    };

    chart.setOption(option, true);

    setTimeout(() => {
      drawThresholdLine(chart);
    }, 0);

    const handleResize = () => {
      chart.resize();

      setTimeout(() => {
        drawThresholdLine(chart);
      }, 0);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [
    chartData,
    yAxisMax,
    safeThresholdIndex,
    thresholdValues.length,
    theme,
  ]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
      chartInstanceRef.current = null;
    };
  }, []);

  return (
    <div style={{ width: "100%" }}>
      <div ref={chartRef} style={{ width: "100%", height: 235 }} />
    </div>
  );
};

export default WirelessChannelInterfaceSliderChart;